// Theme system types for flexible tenant configuration

export interface ColorScheme {
  primary: string
  secondary: string
  accent: string
  background: string
  text: string
  border: string
  success: string
  warning: string
  danger: string
  info: string
}

export interface BrandingConfig {
  siteName: string
  logoUrl: string
  faviconUrl: string
  footerText?: string
}

export interface ThemeAssets {
  baseTheme: 'classic' | 'modern'  // Layout structure
  colorScheme: ColorScheme
  branding: BrandingConfig
  customCSS?: string  // Additional custom CSS if needed
}

export interface TenantContentBlock {
  type: 'text' | 'image'
  textContent: string
  imageUrl: string
}

export interface TenantThemeConfig {
  tenantId: string
  themeId: string  // Theme identifier for CSS loading
  theme: ThemeAssets
  settings: {
    marqueeText: string
    /** ข้อความต้อนรับ (meta / OG) — fallback เป็น marqueeText */
    welcomeMessage?: string
    contactInfo?: {
      line?: string
      lineUrl?: string
      phone?: string
      email?: string
    }
    /** When true or undefined, show web chat widget for authenticated users (same as user-classic) */
    webChatEnabled?: boolean
    /** นาทีที่เหลือก่อนปิดรับ — แสดง class wip_wup เมื่อเหลือเวลาน้อยกว่าค่านี้ (หวยยี่กี), ปรับจาก frontend-admin */
    wipWupMinutes?: number
    /** นาทีที่เหลือก่อนปิดรับ — แสดง class wip_wup เมื่อเหลือเวลาน้อยกว่าค่านี้ (หวยอื่นๆ ในรายการหวยวันนี้) */
    wipWupMinutesOther?: number
    /** เปิดการฝากเงินทั้งระบบ — false = แสดงปิดการฝากเงินชั่วคราว (default true) */
    depositEnabled?: boolean
    /** เปิดการถอนเงินทั้งระบบ — false = แสดงปิดการถอนเงินชั่วคราว (default true) */
    withdrawEnabled?: boolean
    /** เปิดการเข้าเล่นหวยทั้งระบบ — false = ปิดปรับปรุงระบบหวย (บล็อกแทงหวย/ยี่กี/ชุด) (default true) */
    lotteryEnabled?: boolean
    /** เปิดการเข้าเล่นเกมส์ กีฬา คาสิโนทั้งระบบ — false = ปิดปรับปรุงระบบเกมส์ (default true) */
    casinoEnabled?: boolean
    /** นาทีที่ไม่ได้ใช้งานแล้วให้ logout อัตโนมัติ แล้ว redirect ไป /login (0 = ปิด, default 30) */
    sessionIdleMinutes?: number
    /** เปิดปุ่มลงชื่อเข้าใช้ด้วย LINE บนหน้า Login (user-modern) — ตั้งจาก admin Tenant Content */
    lineLoginEnabled?: boolean
    /** เปิดใช้ OTP (SMS) สำหรับสมัครสมาชิก/ยืนยันเบอร์ — false = ซ่อนปุ่มขอ OTP และขั้นตอน OTP */
    smsOtpEnabled?: boolean
    /** ช่องถ่ายทอดสดอิสระ (บอล/กีฬา) */
    liveTvChannels?: Array<{
      id: string
      title: string
      youtubeUrl: string
      enabled: boolean
      category?: 'football' | 'lottery' | 'other'
      sortOrder?: number
    }>
    /** เปิดหน้า Live TV และปุ่มลอย (default true เมื่อมีช่อง active) */
    liveTvEnabled?: boolean
    /** เปิดระบบทายผล fixed odds (platform owner ควบคุมจาก tenant-admin) */
    predictionEnabled?: boolean
    /** YouTube Live ต่อหวย (key = huayCode uppercased / huayId) — global ตั้งจาก /owner-live-stream */
    huayYoutubeLive?: Record<string, {
      youtubeUrl: string
      enabled: boolean
      label?: string
      updatedAt?: string
    }>
    /** Global player default from /owner-live-stream — true = start muted on autoplay */
    liveTvPlayerStartMuted?: boolean
    /** อัตราการจ่าย — จาก tenant content (ข้อความหรือรูปภาพ) */
    paymentRates?: TenantContentBlock
  }
}

// Pre-defined color schemes for easy setup
export const COLOR_SCHEMES: Record<string, ColorScheme> = {
  'asia-green': {
    primary: '#28a745',
    secondary: '#17a2b8',
    accent: '#ffc107',
    background: '#f8f9fa',
    text: '#212529',
    border: '#dee2e6',
    success: '#28a745',
    warning: '#ffc107',
    danger: '#dc3545',
    info: '#17a2b8'
  },
  'fast-blue': {
    primary: '#007bff',
    secondary: '#6c757d',
    accent: '#17a2b8',
    background: '#f8f9fa',
    text: '#212529',
    border: '#dee2e6',
    success: '#28a745',
    warning: '#ffc107',
    danger: '#dc3545',
    info: '#17a2b8'
  },
  'today-modern-blue': {
    primary: '#1e3a8a',
    secondary: '#1e40af',
    accent: '#3b82f6',
    background: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%)',
    text: '#ffffff',
    border: '#3b82f6',
    success: '#10b981',
    warning: '#f59e0b',
    danger: '#ef4444',
    info: '#06b6d4'
  },
  'today-modern-red': {
    primary: '#dc2626',
    secondary: '#991b1b',
    accent: '#ef4444',
    background: 'linear-gradient(135deg, #dc2626 0%, #991b1b 100%)',
    text: '#ffffff',
    border: '#ef4444',
    success: '#10b981',
    warning: '#f59e0b',
    danger: '#ef4444',
    info: '#06b6d4'
  },
  'today-modern-lightblue': {
    primary: '#0ea5e9',
    secondary: '#0284c7',
    accent: '#38bdf8',
    background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
    text: '#ffffff',
    border: '#38bdf8',
    success: '#10b981',
    warning: '#f59e0b',
    danger: '#ef4444',
    info: '#06b6d4'
  }
}
